import FetchWrapper from '../Common/Wrappers/FetchWrapper';
import { Service } from '../Common/service.enum';
import Logging from '../../Logging/Logging';
import {
  CertificateStatus,
  CertificateRejectionDetail,
  CertificateReplaceStatusState,
  CertificateReplaceStatusAdditionalInfo,
  CertificateReplaceStatusReasons,
  CertificateReplaceStatusSendEmail,
  CertificateToken,
  Certificate,
  CertificateSummaryCollection,
  CertificateReplaceExcludeFromSync,
  CertificateUpdate,
  S3UploadRequest,
  S3UploadResponse,
} from '../../..';
import {
  filtersToStandardQueryParams,
  sortModelToStandardQueryParam,
} from '../Common/filterSorting';

export default class CertificateRepo {
  private fetch: FetchWrapper;

  constructor(fetch: FetchWrapper) {
    this.fetch = fetch;
  }

  getCertificatesV41(
    startRow:number,
    endRow:number,
    filterModel: string[] = null,
    sortModel: string[] = null,
  ): Promise<CertificateSummaryCollection> {
    const apiServiceEndpoint = 'portal/v4.1/certificates';
    const filterQueryParams = filtersToStandardQueryParams(filterModel);
    const sortQueryParam = sortModelToStandardQueryParam(sortModel);
    const numberRows = endRow > startRow ? endRow - startRow : 0;
    const pagingQueryParam = `$top=${numberRows}&$skip=${startRow}&$count=true`;
    let queryParams = `?${pagingQueryParam}`;
    if (filterQueryParams && sortQueryParam) {
      queryParams = `?${filterQueryParams}&${sortQueryParam}&${pagingQueryParam}`;
    } else if (filterQueryParams) {
      queryParams = `?${filterQueryParams}&${pagingQueryParam}`;
    } else if (sortQueryParam) {
      queryParams = `?${sortQueryParam}&${pagingQueryParam}`;
    }
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<CertificateSummaryCollection>(Service.ECM, url);
  }

  getCertificateV41(certificateUuid: string): Promise<Certificate> {
    const apiServiceEndpoint = `portal/v4.1/certificates/${certificateUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<Certificate>(Service.ECM, url);
  }

  getCertificateTokenV41(certificateUuid: string): Promise<CertificateToken> {
    const apiServiceEndpoint = `portal/v4.1/certificates/${certificateUuid}/token`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<CertificateToken>(Service.ECM, url);
  }

  updateCertificateV41(certificateUuid: string, updates: CertificateUpdate []): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/certificates/${certificateUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('PATCH', url);
    return this.fetch.patchToService<void>(Service.ECM, url, updates);
  }

  approveUserCertificateV41(certificateUuid: string): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/certificates/${certificateUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    const request: CertificateReplaceStatusState [] = [];

    // Replace the Status of the Request
    const certificateReplaceStatus: CertificateReplaceStatusState = {
      op: 'replace',
      path: '/status/state',
      value: CertificateStatus.Approved,
    };
    request.push(certificateReplaceStatus);

    Logging.Info('PATCH', url, request);
    return this.fetch.patchToService<void>(Service.ECM, url, request);
  }

  rejectUserCertificateV41(certificateUuid: string, rejectionDetail: CertificateRejectionDetail): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/certificates/${certificateUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    const request: (CertificateReplaceStatusState | CertificateReplaceStatusAdditionalInfo
      | CertificateReplaceStatusReasons | CertificateReplaceStatusSendEmail) [] = [];

    // Add the Additional Info to the Request
    const certificateReplaceStatusAdditionalInfo: CertificateReplaceStatusAdditionalInfo = {
      op: 'add',
      path: '/status/additionalInfo',
      value: rejectionDetail.additionalInfo,
    };
    request.push(certificateReplaceStatusAdditionalInfo);

    // Add the Reasons to the Request
    const certificateReplaceStatusReasons: CertificateReplaceStatusReasons = {
      op: 'add',
      path: '/status/reasons',
      value: rejectionDetail.reasons.join(','),
    };
    request.push(certificateReplaceStatusReasons);

    // Add the Send Email to the Request
    const certificateReplaceStatusSendEmail: CertificateReplaceStatusSendEmail = {
      op: 'add',
      path: '/status/sendEmail',
      value: rejectionDetail.sendEmail,
    };
    request.push(certificateReplaceStatusSendEmail);

    // Replace the Status of the Request
    const certificateReplaceStatusState: CertificateReplaceStatusState = {
      op: 'replace',
      path: '/status/state',
      value: CertificateStatus.Rejected,
    };
    request.push(certificateReplaceStatusState);

    Logging.Info('PATCH', url, request);
    return this.fetch.patchToService<void>(Service.ECM, url, request);
  }

  excludeFromSyncCertficateV41(certficateUuid: string, excludeFromSync: boolean): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/certificates/${certficateUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    const request: CertificateReplaceExcludeFromSync [] = [];

    // Replace the Exclude from Sync of the Certificate
    const certificateReplaceExcludeFromSync: CertificateReplaceExcludeFromSync = {
      op: 'replace',
      path: '/excludeFromSync',
      value: excludeFromSync,
    };
    request.push(certificateReplaceExcludeFromSync);

    Logging.Info('PATCH', url, request);
    return this.fetch.patchToService<void>(Service.ECM, url, request);
  }

  certificateS3UploadV41(upload: S3UploadRequest): Promise<S3UploadResponse> {
    const apiServiceEndpoint = 'portal/v4.1/certificate-upload';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('POST', url, upload);
    return this.fetch.postToService<S3UploadResponse>(Service.ECM, url, upload);
  }

  addCertificateV41(certificate: Certificate): Promise<Headers> {
    const apiServiceEndpoint = 'portal/v4.1/certificates';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('POST', url, certificate);
    return this.fetch.postToServiceReturnHeaders<void>(Service.ECM, url, certificate);
  }
}
